/** Serializes feed writes by account, workspace, and conversation so overlapping scopes run in order. */
export class WriteQueue {
  private readonly conversationChains = new Map<string, Promise<unknown>>();
  private readonly accountChains = new Map<string, Promise<unknown>>();
  private readonly workspaceChains = new Map<string, Promise<unknown>>();

  /**
   * Queues a conversation write after prior conversation/account writes settle.
   * @template T Result type returned by the task.
   */
  enqueue<T>(accountId: string, conversationId: string, task: () => Promise<T>): Promise<T> {
    const key = writeQueueKey(accountId, conversationId);
    const previous = waitForSettled([
      this.conversationChains.get(key),
      this.accountChains.get(accountId)
    ]);
    const run = previous.then(task);
    this.setChain(this.conversationChains, key, run);
    return run;
  }

  /**
   * Queues an account write after active account, conversation, and workspace writes settle.
   * @template T Result type returned by the task.
   */
  enqueueAccount<T>(accountId: string, task: () => Promise<T>): Promise<T> {
    const activeConversationChains = [...this.conversationChains]
      .filter(([key]) => key.startsWith(writeQueueAccountPrefix(accountId)))
      .map(([, chain]) => chain);
    const activeWorkspaceChains = [...this.workspaceChains]
      .filter(([key]) => key.startsWith(writeQueueAccountPrefix(accountId)))
      .map(([, chain]) => chain);
    const previous = waitForSettled([
      this.accountChains.get(accountId),
      ...activeConversationChains,
      ...activeWorkspaceChains
    ]);
    const run = previous.then(task);
    this.setChain(this.accountChains, accountId, run);
    return run;
  }

  /**
   * Queues a workspace write after prior workspace/account writes settle.
   * @template T Result type returned by the task.
   */
  enqueueWorkspace<T>(accountId: string, workspaceId: string, task: () => Promise<T>): Promise<T> {
    const key = writeQueueWorkspaceKey(accountId, workspaceId);
    const previous = waitForSettled([
      this.workspaceChains.get(key),
      this.accountChains.get(accountId)
    ]);
    const run = previous.then(task);
    this.setChain(this.workspaceChains, key, run);
    return run;
  }

  private setChain<T>(chains: Map<string, Promise<unknown>>, key: string, run: Promise<T>): void {
    const chain = run.catch(() => undefined);
    chains.set(key, chain);
    void chain
      .then(() => {
        if (chains.get(key) === chain) {
          chains.delete(key);
        }
      })
      .catch(() => undefined);
  }
}

function waitForSettled(chains: readonly (Promise<unknown> | undefined)[]): Promise<void> {
  const activeChains = chains.filter((chain): chain is Promise<unknown> => chain !== undefined);
  if (activeChains.length === 0) return Promise.resolve();
  return Promise.allSettled(activeChains).then(() => undefined);
}

function writeQueueKey(accountId: string, conversationId: string): string {
  return `${writeQueueAccountPrefix(accountId)}${conversationId}`;
}

function writeQueueWorkspaceKey(accountId: string, workspaceId: string): string {
  return `${writeQueueAccountPrefix(accountId)}workspace:${workspaceId}`;
}

function writeQueueAccountPrefix(accountId: string): string {
  return `${accountId}:`;
}
